<template>
  <!-- MODAL -->
  <TransitionRoot as="template" :show="open">
    <Dialog class="relative z-50" @close="closeDialog">
      <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0" enter-to="opacity-100" leave="ease-in duration-200" leave-from="opacity-100" leave-to="opacity-0">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
      </TransitionChild>

      <div class="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div class="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
          <TransitionChild as="template" enter="ease-out duration-300" enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enter-to="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leave-from="opacity-100 translate-y-0 sm:scale-100" leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
            <DialogPanel class="relative transform overflow-hidden rounded-lg bg-white px-8 pb-8 pt-6 text-left shadow-xl transition-all sm:my-8 sm:w-full lg:max-w-4xl sm:p-6">
              <!-- Conteiner de título e botão de fechar -->
              <div class="flex justify-between items-start">
                <h2 class="text-xl font-bold leading-9 tracking-tight text-gray-900">{{ title }}</h2>
                <button @click="closeDialog" type="button" class="text-gray-400 hover:text-gray-600 -mt-4">
                  <span class="sr-only">Fechar</span>
                  <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <!-- Formulário principal com flex -->
              <div class="mt-4">
                <form class="flex flex-wrap gap-4" @submit="sendData" method="POST">
                  <div v-for="(input, index) in inputsList" :key="index" class="flex-1 min-w-[300px] sm:min-w-[300px] lg:w-1/2">
                    <label :for="input.name" class="block text-sm font-medium leading-6 text-gray-900">{{ input.label }}</label>
                    <input
                      v-model="data[input.name]"
                      :id="input.name"
                      :name="input.name"
                      :type="input.type"
                      :required="true"
                      class="mt-2 block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
                    />
                  </div>
                  <!-- Área de planos -->
                  <div class="w-full mt-6">
                    <h3 class="text-lg font-semibold text-gray-800 mb-2">Planos</h3>

                    <!-- Lista de planos adicionados -->
                    <ul v-if="data?.planos?.length" class="space-y-2 mb-4">
                      <li
                        v-for="(plano, index) in data.planos"
                        :key="index"
                        class="flex justify-between items-center border p-3 rounded-md bg-gray-50"
                      >
                        <div>
                          <p class="font-medium text-gray-800">{{ plano.nome }} – R$ {{ plano.valor }}</p>
                          <p class="text-sm text-gray-500">{{ plano.descricao }}</p>
                          <p class="text-xs text-gray-400">Faturamento: {{ plano.regra_de_faturamento }}</p>
                        </div>
                        <button @click="removePlano(index)" class="text-red-500 hover:text-red-700 text-sm">Remover</button>
                      </li>
                    </ul>

                    <!-- Botão para mostrar o formulário -->
                    <button
                      type="button"
                      @click="showPlanoForm = true"
                      v-if="!showPlanoForm"
                      class="inline-flex items-center px-3 py-1.5 rounded-md bg-customPurple text-white text-sm hover:bg-sienaBlue"
                    >
                      Adicionar Plano
                    </button>

                    <!-- Formulário de novo plano -->
                    <div
                      v-if="showPlanoForm"
                      class="grid grid-cols-1 sm:grid-cols-2 gap-4 border p-4 rounded-md bg-white shadow-sm mt-4"
                    >
                      <div>
                        <label class="block text-sm font-medium text-gray-700">Nome</label>
                        <input v-model="novoPlano.nome" type="text" required class="mt-1 block w-full rounded-md border-gray-300 shadow-sm" />
                      </div>

                      <div>
                        <label class="block text-sm font-medium text-gray-700">Descrição</label>
                        <input v-model="novoPlano.descricao" type="text" required class="mt-1 block w-full rounded-md border-gray-300 shadow-sm" />
                      </div>

                      <div>
                        <label class="block text-sm font-medium text-gray-700">Valor</label>
                        <input v-model="novoPlano.valor" type="number" step="0.01" required class="mt-1 block w-full rounded-md border-gray-300 shadow-sm" />
                      </div>

                      <div>
                        <label class="block text-sm font-medium text-gray-700">Regra de Faturamento</label>
                        <input v-model="novoPlano.regra_de_faturamento" type="text" required class="mt-1 block w-full rounded-md border-gray-300 shadow-sm" />
                      </div>

                      <div>
                        <label class="block text-sm font-medium text-gray-700">Tipo de Plano</label>
                        <select v-model="novoPlano.tipo_plano" required class="mt-1 block w-full rounded-md border-gray-300 shadow-sm">
                          <option value="">Selecione</option>
                          <option value="Vida em Grupo">Vida em Grupo</option>
                          <option value="Vida Individual">Vida Individual</option>
                        </select>
                      </div>

                      <div class="col-span-2">
                        <label class="block text-sm font-medium text-gray-700">Informações da Seguradora</label>
                        <textarea
                          v-model="novoPlano.informacoes_seguradora"
                          rows="3"
                          class="mt-1 block w-full rounded-md border-gray-300 shadow-sm"
                        ></textarea>
                      </div>

                      <!-- Ações -->
                      <div class="col-span-2 flex justify-end gap-3 mt-4">
                        <button
                          type="button"
                          @click="cancelarPlano"
                          class="text-sm text-gray-500 hover:text-gray-700"
                        >
                          Cancelar
                        </button>
                        <button
                          type="button"
                          @click="confirmarPlano"
                          class="bg-green-600 text-white text-sm px-4 py-1.5 rounded-md hover:bg-green-500"
                        >
                          Adicionar
                        </button>
                      </div>
                    </div>
                  </div>
                  <div class="w-full">
                    <button type="submit" class="flex w-full justify-center rounded-md bg-customPurple px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm hover:bg-sienaBlue">Confirmar</button>
                  </div>
                </form>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script>

import { Dialog, DialogPanel, TransitionChild, TransitionRoot } from '@headlessui/vue';

  export default {
    name: 'EmpresasForm',
    async created(){
        
    },
    props: {
      inputsList: {
        type: Array,
        required: true
      },
      title: {
        type: String,
        required: true
      },
      open: {
        type: Boolean,
        required: true
      },
      resetFormData:{
        type: Boolean,
        required:true
      },
    },  
    watch: {
      resetFormData(newValue) { 
        if(newValue == true){
          this.data = {}
        }
      }
    }, 
    data() {
      return {
        data:
        {  
           planos: []
        },
        novoPlano: {
          nome: '',
          descricao: '',
          valor: null,
          regra_de_faturamento: ''
        },
        showPlanoForm: false
      };
    },
    methods: {
      sendData(e) {
        e.preventDefault();
        this.$emit('data', this.data);
      },
      closeDialog() {
        this.$emit('update-open', false);  // Emite um evento para o componente pai alterar a prop "open"
      },
      confirmarPlano() {
        const { nome, descricao, valor, regra_de_faturamento } = this.novoPlano;

        if (!nome || !descricao || !valor || !regra_de_faturamento) {
          alert('Preencha todos os campos do plano.');
          return;
        }

        this.data.planos.push({ ...this.novoPlano });
        this.novoPlano = {
          nome: '',
          descricao: '',
          valor: null,
          regra_de_faturamento: ''
        };
        this.showPlanoForm = false;
      },
      cancelarPlano() {
        this.showPlanoForm = false;
        this.novoPlano = {
          nome: '',
          descricao: '',
          valor: null,
          regra_de_faturamento: ''
        };
      },
      removePlano(index) {
        this.data.planos.splice(index, 1);
      }
    },
    components:{
      Dialog,
      DialogPanel, 
      TransitionChild, 
      TransitionRoot,
    },
  }
</script>